@if ((vm$ | async); as vmState) {
<section class="record-card">
  <!-- Card Header: Search + Actions -->
  <div class="record-card__header">
    <div class="record-card__search">
      <div
        class="search-pill"
        [class.expanded]="searchExpanded()"
        role="search"
        (click)="!searchExpanded() && expandSearch()"
      >
        @if (!searchExpanded()) {
          <mat-icon class="search-pill__icon">search</mat-icon>
          <span class="search-pill__placeholder" i18n="@@home.userRecordList.search.placeholder">フィルター</span>
        } @else {
          <mat-icon class="search-pill__icon search-pill__icon--active">search</mat-icon>
          <input
            #searchInput
            type="text"
            class="search-pill__input"
            [value]="filterText"
            (input)="onFilterInput($event)"
            (blur)="collapseSearch()"
            placeholder="フィルター"
            i18n-placeholder="@@home.userRecordList.search.inputPlaceholder"
            aria-label="フィルター"
            i18n-aria-label="@@home.userRecordList.search.ariaLabel"
          />
          @if (filterText.trim()) {
            <button
              mat-icon-button
              class="search-pill__clear"
              (mousedown)="$event.preventDefault()"
              (click)="clearSearch($event)"
              aria-label="検索をクリア"
              i18n-aria-label="@@home.userRecordList.search.clearAriaLabel"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        }
      </div>
    </div>
    <div class="record-card__actions">
      @if (vmState.protocolFilterOptions.length > 0) {
        <button
          mat-icon-button
          class="record-card__action-btn"
          [matMenuTriggerFor]="protocolMenu"
          matTooltip="表示プロトコルの切替"
          aria-label="表示プロトコルの切替"
          i18n-matTooltip="@@home.userRecordList.tooltip.protocolFilters"
          i18n-aria-label="@@home.userRecordList.ariaLabel.protocolFilters"
        >
          <mat-icon>filter_alt</mat-icon>
        </button>
        <mat-menu #protocolMenu="matMenu" class="column-menu protocol-menu">
          <div class="column-menu__content protocol-menu__content" (click)="$event.stopPropagation()">
            <span class="column-menu__label" i18n="@@home.userRecordList.label.showHideProtocol">表示プロトコル</span>
            <div class="protocol-menu__actions">
              <button
                type="button"
                class="protocol-menu__action"
                (click)="selectAllProtocols()"
                [disabled]="areAllProtocolsSelected(vmState)"
                i18n="@@home.userRecordList.button.selectAllProtocols"
              >
                全て選択
              </button>
              <button
                type="button"
                class="protocol-menu__action"
                (click)="clearAllProtocols()"
                [disabled]="areNoProtocolsSelected(vmState)"
                i18n="@@home.userRecordList.button.clearAllProtocols"
              >
                全て解除
              </button>
            </div>
            <mat-divider></mat-divider>
            @for (option of vmState.protocolFilterOptions; track option.code) {
              <mat-checkbox
                [checked]="isProtocolSelected(vmState, option.code)"
                (change)="onProtocolFilterChange(option.code, $event.checked)"
                color="primary"
                [matTooltip]="option.tooltip"
                class="protocol-menu__checkbox"
              >
                {{ option.label }}
                @if (option.secondaryLabel) {
                  （{{ option.secondaryLabel }}）
                }
              </mat-checkbox>
            }
          </div>
        </mat-menu>
      }
      <!-- Column Show/Hide -->
      <button
        mat-icon-button
        class="record-card__action-btn"
        [matMenuTriggerFor]="columnMenu"
        matTooltip="表示項目の切替"
        aria-label="表示項目の切替"
        i18n-matTooltip="@@home.userRecordList.tooltip.columns"
        i18n-aria-label="@@home.userRecordList.ariaLabel.columns"
      >
        <mat-icon>filter_list</mat-icon>
      </button>
      <mat-menu #columnMenu="matMenu" class="column-menu">
        <div class="column-menu__content" (click)="$event.stopPropagation()">
          <span class="column-menu__label" i18n="@@home.userRecordList.label.showHideColumn">カラム表示/非表示</span>
          <mat-divider></mat-divider>
          @for (cd of vmState.columnDefinitions; track cd.def) {
            @if (!cd.locked && cd.label) {
              <mat-checkbox
                [checked]="cd.visible"
                (change)="toggleColumnVisibility(cd.def)"
                color="primary"
              >
                {{ cd.label }}
              </mat-checkbox>
            }
          }
        </div>
      </mat-menu>

      <!-- Refresh -->
      <button
        mat-icon-button
        class="record-card__action-btn"
        (click)="onRefresh()"
        [disabled]="isLoadingRecordList"
        matTooltip="リストを更新"
        i18n-matTooltip="@@home.userRecordList.button.refresh"
        aria-label="リストを更新"
        i18n-aria-label="@@home.userRecordList.ariaLabel.refresh"
      >
        <mat-icon [class.spinning]="isLoadingRecordList">refresh</mat-icon>
      </button>
    </div>
  </div>

  <!-- Card Body: Table or Skeleton -->
  @if (isLoadingRecordList) {
    <div class="record-card__body">
      <app-table-skeleton [rows]="7" />
    </div>
  } @else {
    <div
      class="record-card__body"
      #scrollContainer
      (scroll)="onTableScroll()"
    >
      <table
        mat-table
        class="record-list-table"
        [dataSource]="vmState.pagedRows"
        matSort
        matSortDisableClear
        [matSortActive]="vmState.sortActive"
        [matSortDirection]="vmState.sortDirection"
        (matSortChange)="onSortChange($event)"
      >
        <ng-container matColumnDef="createdAt">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.createdAt">
            計測日時
          </th>
          <td mat-cell *matCellDef="let row" class="table-cell-date">
            @if (row.kind === 'record') {
              {{ row.record.data.createdAtLocalStr | slice:0:16 }}
            } @else {
              {{ row.media.data.createdAt | date:'yyyy/MM/dd HH:mm' }}
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="recordType">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.recordType">
            プロトコル
          </th>
          <td mat-cell *matCellDef="let row" class="table-cell-protocol">
            @if (row.kind === 'media') {
              <span class="protocol-cell">
                <span class="protocol-cell__primary" i18n="@@home.userRecordList.label.mediaRecord">メディア記録</span>
                <span class="protocol-cell__secondary">{{ row.media.data.method }}</span>
              </span>
            } @else {
              <span
                class="protocol-cell"
                [matTooltip]="getProtocolTooltip(row)"
                [matTooltipDisabled]="!getProtocolSecondaryLabel(row)"
              >
                <span class="protocol-cell__primary">{{ getProtocolPrimaryLabel(row) }}</span>
                @if (getProtocolSecondaryLabel(row); as secondaryLabel) {
                  <span class="protocol-cell__secondary">{{ secondaryLabel }}</span>
                }
              </span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="userName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.userName">
            ユーザー
          </th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              {{ row.record.meta.user.displayName }}
            } @else {
              -
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="measurerName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.measurerName">
            スタッフ
          </th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              {{ row.record.meta.measurer.lastName + ' ' + row.record.meta.measurer.firstName }}
            } @else {
              -
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="numOfSwallowing">
          <th mat-header-cell *matHeaderCellDef i18n="@@home.userRecordList.column.numOfSwallowing">
            嚥下回数
          </th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              {{ formatMetricValue(row.record.data.numOfSwallowings) }}
            } @else {
              -
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="meanSwallowingTime">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.meanSwallowingTime">
            平均嚥下時間（秒）
          </th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              {{ formatMeanSwallowingTime(row.record.data.swallowingsTimeAverage) }}
            } @else {
              -
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="sd">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.sd">
            標準偏差（ばらつき）
          </th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              {{ row.record.data.swallowingsTimeVariance }}
            } @else {
              -
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="coughPresence">
          <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@home.userRecordList.column.coughPresence">
            せき・むせの有無
          </th>
          <td mat-cell *matCellDef="let row">
            {{ getCoughPresenceLabel(row) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="wavDuration">
          <th mat-header-cell *matHeaderCellDef i18n="@@home.userRecordList.column.wavDuration">
            記録時間
          </th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              {{ row.record.data.wavDuration | millisecondsToTime }}
            } @else {
              -
            }
          </td>
        </ng-container>

        <!-- Download Column (always visible) -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef i18n="@@home.userRecordList.column.actions">操作</th>
          <td mat-cell *matCellDef="let row">
            @if (row.kind === 'record') {
              <button
                mat-icon-button
                class="record-download-btn"
                (click)="onDownload($event, row)"
                matTooltip="ダウンロード"
                aria-label="ダウンロード"
                i18n-matTooltip="@@home.userRecordList.tooltip.rawDataDownload"
                i18n-aria-label="@@home.userRecordList.ariaLabel.rawDataDownload"
              >
                <mat-icon aria-hidden="true">download</mat-icon>
              </button>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="vmState.displayedColumns; sticky: true"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: vmState.displayedColumns"
          [routerLink]="getRowDetailLink(vmState, row)"
          [queryParams]="getRowQueryParams(row)"
          [class.media-row]="row.kind === 'media'"
          [attr.tabindex]="vmState.section && vmState.selectedUserWithUserData ? 0 : null"
          (keydown.enter)="navigateToRow(row)"
          role="link"
        ></tr>
      </table>
    </div>

    <!-- Paginator inside card -->
    <mat-paginator
      class="record-card__paginator"
      [length]="vmState.filteredRows.length"
      [pageIndex]="vmState.pageIndex"
      [pageSize]="vmState.pageSize"
      [pageSizeOptions]="[5, 10, 25, 100]"
      (page)="onPaginatorChange($event)"
    ></mat-paginator>
  }
</section>
}
